import { useContext } from 'react';

import { useTheme } from 'styled-components';

import { useFlag } from '@unleash/proxy-client-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { buildGoogleCalendarLink } from 'utils/buildGoogleCalendarLink';
import { FeatureToggle } from 'utils/unleash/toggles';

import { useModals, NotificationContext } from 'context';
import { useTimeFormat } from 'hooks';
import { getSessionLink } from 'services';

import { Modal } from 'components';
import { CancelEventMobileComponentProps } from 'modals/CancelEventMobile';
import { ConfirmComponentProps } from 'modals/Confirm';

import { CalendarEventProps } from './types';
// eslint-disable-next-line import/order
import { YogaTheme } from 'types/others';

import * as S from './styles';

const CalendarEvent: React.FC<CalendarEventProps> = ({
  isOpen,
  modalKey,
  props,
  onClose,
}) => {
  const intl = useIntl();
  const theme: YogaTheme = useTheme();
  const { openModal, closeModal } = useModals();
  const { formatTimeInterval } = useTimeFormat();
  const { setNotification } = useContext(NotificationContext);

  const isRescheduleToggleEnabled = useFlag(FeatureToggle.Reschedule);

  if (!props) return null;

  const {
    endUserFullName,
    startAt,
    endAt,
    endUserCognitoId,
    bookingId,
    onConfirmDelete,
    sessionID,
  } = props;

  async function joinSession(userCognitoId: string) {
    try {
      const { url } = await getSessionLink(userCognitoId);
      window.open(url, '_blank');
    } catch (e) {
      if (e instanceof Error && Number(e.message) === 403) {
        setNotification({
          message: intl.formatMessage({
            id: 'CALENDAR_EVENT.TOAST.WARN',
          }),
          type: 'failure',
        });

        return;
      }

      setNotification({
        message: intl.formatMessage({
          id: 'CALENDAR_EVENT.TOAST.ERROR',
        }),
        type: 'failure',
      });
    }
  }

  function handleDeleteSession(bookingID: string) {
    closeModal('calendarEvent');

    if (isRescheduleToggleEnabled) {
      return openModal<CancelEventMobileComponentProps>('cancelEventMobile', {
        props: {
          bookingID,
          sessionID,
          sessionStartAt: startAt,
          patientCognitoId: endUserCognitoId,
          onConfirmDelete: () => onConfirmDelete(bookingID),
        },
      });
    }

    return openModal<ConfirmComponentProps>('confirm', {
      props: {
        message: intl.formatMessage({
          id: 'CALENDAR_EVENT.DELETE_BOOKING_MODAL_TEXT',
        }),
        confirmLabel: intl.formatMessage({
          id: 'CALENDAR_EVENT.CANCEL_CONFIRM',
        }),
        onConfirm: () => {
          onConfirmDelete(bookingID);
        },
        hideIcon: false,
      },
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onAfterClose={() => {
        if (onClose) onClose();
      }}
      closeModal={() => closeModal(modalKey)}
      overlayStyle={{ alignContent: 'center' }}
      contentStyle={{
        maxHeight: `${theme.yoga.sizes.calendarEventContainerHeight}px`,
      }}
    >
      <S.Container data-testid="CalendarEventContainer">
        <S.Content data-testid="CalendarEventContent">
          <S.HeaderContainer>
            <S.Title data-testid="CalendarEventTitle">
              <FormattedMessage id={endUserFullName} />
            </S.Title>
            <S.SubTitle data-testid="CalendarEventSubTitle">
              {formatTimeInterval(startAt, endAt)}
            </S.SubTitle>
          </S.HeaderContainer>
          <S.CalendarSessionEventButton
            target="_blank"
            href={`/new-provider/enduser/${endUserCognitoId}`}
            data-testid="CalendarSessionPersonalInfo"
          >
            <S.StyledAlertCircle />
            <FormattedMessage id="CALENDAR_EVENT.PATIENT_INFO" />
          </S.CalendarSessionEventButton>
          <S.CalendarSessionEventButton
            data-testid="CalendarSessionPersonalCalendar"
            onClick={() =>
              buildGoogleCalendarLink(startAt, endAt, endUserFullName)
            }
          >
            <S.StyledBookingFilled />
            <FormattedMessage id="CALENDAR_EVENT.PERSONAL_CALENDAR" />
          </S.CalendarSessionEventButton>
          <S.Footer>
            <S.StyledButton
              data-testid="CalendarSessionJoinSession"
              onClick={() => joinSession(endUserCognitoId)}
            >
              <FormattedMessage id="CALENDAR_EVENT.JOIN_SESSION" />
            </S.StyledButton>
            <S.CalendarSessionFooterButton
              data-testid="CalendarSessionCancelSession"
              onClick={() => handleDeleteSession(bookingId)}
            >
              <FormattedMessage id="CALENDAR_EVENT.CANCEL_BOOKING" />
            </S.CalendarSessionFooterButton>
          </S.Footer>
        </S.Content>
      </S.Container>
    </Modal>
  );
};

export default CalendarEvent;
